<template>
  <div class="col-12 col-xl-12">
    
    <!-- Page Card -->
    <div class="card mb-5 mt-5 overflow-visible">
      <!-- Header -->
      <div class="card-header p-0 position-relative mx-3 z-index-2" style="height: 64px">
        <div class="bg-gradient-primary shadow-primary border-radius-lg h-100 d-flex align-items-center">
          <h6 class="text-white text-capitalize ps-3 pe-3 m-0">
            {{ $t('student_rewards.history.title') }} — {{ student?.full_name || $t('student_rewards.student') }}
          </h6>
        </div>
      </div>

      <!-- Body -->
      <div class="card-body px-0 pb-2">
        <!-- Filters Section -->
        <div class="container-fluid px-0">
          <div class="row mb-4 g-2 p-3 p-xl-4">
            <!-- Academic Year Filter -->
            <div class="col-md-4">
              <select
                class="form-select form-select-sm"
                v-model="localFilters.academic_year_id"
                @change="applyFilters"
              >
                <option value="">{{ $t('student_rewards.filters.all_years') }}</option>
                <option v-for="year in academicYears" :key="year.id" :value="year.id">
                  {{ year.name }}
                </option>
              </select>
            </div>

            <!-- Type Filter -->
            <div class="col-md-3">
              <select
                class="form-select form-select-sm"
                v-model="localFilters.type"
                @change="applyFilters"
              >
                <option value="">{{ $t('student_rewards.filters.all_types') }}</option>
                <option value="positive">{{ $t('student_rewards.filters.positive_only') }}</option>
                <option value="negative">{{ $t('student_rewards.filters.negative_only') }}</option>
              </select>
            </div>

            <!-- Reset Filter (desktop) -->
            <div class="col-md-1 d-none d-md-block">
              <button 
                class="btn btn-outline-secondary btn-sm w-100" 
                @click="resetFilters"
                :title="$t('filters.resetTitle')"
              >
                <i class="fas fa-filter-circle-xmark"></i>
              </button>
            </div>

            <!-- Search -->
            <div class="col-md-4 position-relative">
              <input
                type="text"
                class="form-control form-control-sm ps-5"
                :placeholder="$t('student_rewards.history.search_placeholder')"
                v-model="localFilters.search"
                @input="handleSearchInput"
                :dir="$i18n.locale === 'ar' ? 'rtl' : 'ltr'"
                style="padding-right: 12px"
              />
              <span class="position-absolute start-0 top-50 translate-middle-y ps-3">
                <i class="fas fa-search"></i>
              </span>
            </div>

            <!-- Reset Filter (mobile) -->
            <div class="col-md-1 d-md-none">
              <button class="btn btn-outline-secondary btn-sm w-100" @click="resetFilters">
                <i class="fas fa-filter-circle-xmark"></i> 
              </button>
            </div>
          </div>
        </div>

        <!-- Stats -->
        <div class="row g-3 px-3 mb-4">
          <div class="col-12 col-md-4">
            <div class="card shadow-sm stat-card bg-success">
              <div class="card-body d-flex align-items-center justify-content-between">
                <div>
                  <div class="stat-label">{{ $t('student_rewards.positive_points') }}</div>
                  <div class="stat-value">{{ statistics.positive_points || 0 }}</div>
                </div>
                <i class="fas fa-plus-circle fa-lg text-white-75"></i>
              </div>
            </div>
          </div>
          <div class="col-12 col-md-4">
            <div class="card shadow-sm stat-card bg-danger">
              <div class="card-body d-flex align-items-center justify-content-between">
                <div>
                  <div class="stat-label">{{ $t('student_rewards.negative_points') }}</div>
                  <div class="stat-value">{{ statistics.negative_points || 0 }}</div>
                </div>
                <i class="fas fa-minus-circle fa-lg text-white-75"></i>
              </div>
            </div>
          </div>
          <div class="col-12 col-md-4">
            <div class="card shadow-sm stat-card" :class="getTotalPointsClass(statistics.total_points || 0)">
              <div class="card-body d-flex align-items-center justify-content-between">
                <div>
                  <div class="stat-label">{{ $t('student_rewards.total_points') }}</div>
                  <div class="stat-value">{{ statistics.total_points || 0 }}</div>
                </div>
                <i class="fas fa-star fa-lg text-white-75"></i>
              </div>
            </div>
          </div>
        </div>

        <!-- Student Info -->
        <div v-if="student" class="px-3 mb-4">
          <div class="card shadow-sm">
            <div class="card-body">
              <div class="d-flex align-items-center">
                <div class="avatar avatar-xl position-relative me-3">
                  <img
                    :src="student.image_url || '/images/default-avatar.png'"
                    class="rounded-circle"
                    style="width: 60px; height: 60px"
                    @error="handleImageError"
                  />
                </div>
                <div class="flex-grow-1">
                  <h5 class="mb-1">{{ student.full_name }}</h5>
                  <div class="d-flex flex-wrap gap-2">
                    <span class="chip">
                      <i class="fas fa-id-card me-1"></i>
                      {{ student.national_num || student.passport_num || $t('student_rewards.no_id') }}
                    </span>
                    <span class="chip">
                      <i class="fas fa-graduation-cap me-1"></i>
                      {{ student.current_assignment?.class?.name || $t('student_rewards.no_class') }}
                    </span>
                    <span class="chip">
                      <i class="fas fa-users me-1"></i>
                      {{ student.current_assignment?.group?.name || $t('student_rewards.no_group') }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Loading -->
        <LoadingSpinner
          v-if="loading && rewards.length === 0"
          :message="$t('common.loading')"
          class="py-4"
        />

        <!-- Content -->
        <template v-else>
          <!-- Mobile Cards -->
          <div class="d-block d-xl-none px-2">
            <div v-if="!loading && rewards.length === 0" class="text-center py-4 text-muted">
              <i class="fas fa-star fa-2x mb-2"></i>
              <p class="mb-0">{{ $t('student_rewards.history.no_records') }}</p>
            </div>

            <article
              v-for="reward in rewards"
              :key="'mobile-' + reward.id"
              class="card shadow-sm mb-3"
            >
              <div class="card-body py-3">
                <div class="d-flex justify-content-between align-items-start mb-2">
                  <div class="d-flex align-items-center">
                    <div class="avatar avatar-sm me-2">
                      <i :class="[ `fas ${reward.reward_point?.icon || 'fa-star'}`, getPointIconClass(reward.reward_point?.type) ]"></i>
                    </div>
                    <div>
                      <h6 class="mb-0 text-sm">{{ reward.reward_point?.name || 'N/A' }}</h6>
                      <p class="text-xs text-muted mb-0">
                        {{ reward.reward_point?.description || '' }}
                      </p>
                    </div>
                  </div>
                  <span class="badge" :class="getPointBadgeClass(reward.reward_point?.type)">
                    {{ reward.reward_point?.type === 'positive' ? '+' : '-' }}{{ reward.reward_point?.points || 0 }}
                  </span>
                </div>

                <div class="row g-1 mb-2">
                  <div class="col-6">
                    <small class="text-muted d-block">
                      <i class="fas fa-calendar me-1"></i>
                      {{ formatDate(reward.created_at) }}
                    </small>
                  </div>
                  <div class="col-6">
                    <small class="text-muted d-block">
                      <i class="fas fa-clock me-1"></i>
                      {{ formatTime(reward.created_at) }}
                    </small>
                  </div>
                </div>

                <div class="mb-2">
                  <small class="text-muted d-block">
                    <i class="fas fa-user me-1"></i>
                    {{ getAssignedBy(reward) }}
                  </small>
                  <small class="text-muted d-block">
                    <i class="fas fa-graduation-cap me-1"></i>
                    {{ reward.class?.name || 'N/A' }}
                    <span v-if="reward.class_group?.group">
                      ({{ reward.class_group.group.name }})
                    </span>
                  </small>
                  <small class="text-muted d-block">
                    <i class="fas fa-calendar-alt me-1"></i>
                    {{ reward.academic_year?.name || '' }}
                  </small>
                </div>

                <div v-if="reward.notes" class="mt-2 pt-2 border-top">
                  <small class="text-secondary">
                    <i class="fas fa-sticky-note me-1"></i>
                    {{ reward.notes }}
                  </small>
                </div>
              </div>
            </article>
          </div>

          <!-- Desktop Table -->
          <div class="table-responsive p-1 p-xl-4 d-none d-xl-block">
            <table class="table align-items-center mb-0">
              <thead>
                <tr>
                  <th class="text-uppercase text-xxs font-weight-bolder opacity-7">{{ $t('student_rewards.history.date') }}</th>
                  <th class="text-uppercase text-xxs font-weight-bolder opacity-7">{{ $t('student_rewards.history.point') }}</th>
                  <th class="text-uppercase text-xxs font-weight-bolder opacity-7 text-center">{{ $t('student_rewards.history.points') }}</th>
                  <th class="text-uppercase text-xxs font-weight-bolder opacity-7">{{ $t('student_rewards.history.assigned_by') }}</th>
                  <th class="text-uppercase text-xxs font-weight-bolder opacity-7">{{ $t('student_rewards.history.class') }}</th>
                  <th class="text-uppercase text-xxs font-weight-bolder opacity-7">{{ $t('student_rewards.history.notes') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="!loading && rewards.length === 0">
                  <td colspan="6" class="text-center py-5 text-muted">
                    <i class="fas fa-star fa-2x mb-2"></i>
                    <div>{{ $t('student_rewards.history.no_records') }}</div>
                  </td>
                </tr>

                <tr v-for="reward in rewards" :key="reward.id">
                  <td>
                    <div class="d-flex flex-column">
                      <span class="text-xs font-weight-bold">
                        {{ formatDate(reward.created_at) }}
                      </span>
                      <small class="text-muted">{{ formatTime(reward.created_at) }}</small>
                    </div>
                  </td>
                  <td>
                    <div class="d-flex align-items-center">
                      <div class="avatar avatar-sm me-2">
                        <i :class="[ `fas ${reward.reward_point?.icon || 'fa-star'}`, getPointIconClass(reward.reward_point?.type) ]"></i>
                      </div>
                      <div>
                        <h6 class="mb-0 text-sm">{{ reward.reward_point?.name || 'N/A' }}</h6>
                        <p class="text-xs text-secondary mb-0">
                          {{ reward.reward_point?.description || '' }}
                        </p>
                      </div>
                    </div>
                  </td>
                  <td class="align-middle text-center">
                    <span class="badge" :class="getPointBadgeClass(reward.reward_point?.type)">
                      {{ reward.reward_point?.type === 'positive' ? '+' : '-' }}{{ reward.reward_point?.points || 0 }}
                    </span>
                  </td>
                  <td>
                    <div class="d-flex flex-column">
                      <span class="text-xs font-weight-bold">
                        {{ getAssignedBy(reward) }}
                      </span>
                      <small class="text-muted">{{ reward.user?.role?.name.name || '' }}</small>
                    </div>
                  </td>
                  <td>
                    <div class="d-flex flex-column">
                      <span class="text-xs font-weight-bold">
                        {{ reward.class?.name || 'N/A' }} 
                        <span v-if="reward.class_group?.group" class="text-xs text-secondary">
                          ({{ reward.class_group.group.name }})
                        </span>
                      </span>
                      <small class="text-secondary">{{ reward.academic_year?.name || '' }}</small>
                    </div>
                  </td>
                  <td>
                    <span class="text-xs text-secondary">
                      {{ reward.notes || $t('student_rewards.history.no_notes') }}
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, watch, onUnmounted } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useToast } from 'vue-toastification'
import api from '@/api'
import LoadingSpinner from '@/components/common/LoadingSpinner.vue'

const route = useRoute()
const { t } = useI18n()
const toast = useToast()

const loading = ref(false)
const student = ref(null)
const rewards = ref([])
const statistics = ref({
  positive_points: 0,
  negative_points: 0,
  total_points: 0
})
const academicYears = ref([])

// Debounce implementation
let debounceTimeout = null
const DEBOUNCE_DELAY = 500 // 500ms delay

const filters = ref({
  academic_year_id: '',
  type: '',
  search: ''
})

const localFilters = ref({
  ...JSON.parse(JSON.stringify(filters.value))
})


// Clear timeout on unmount
onUnmounted(() => {
  if (debounceTimeout) {
    clearTimeout(debounceTimeout)
  }
})

// Handle search input with debounce
const handleSearchInput = () => {
  // Clear existing timeout
  if (debounceTimeout) {
    clearTimeout(debounceTimeout)
  }
  
  // Set new timeout
  debounceTimeout = setTimeout(() => {
    applyFilters()
    debounceTimeout = null
  }, DEBOUNCE_DELAY)
}

const applyFilters = () => {
  // Update filters with local values
  filters.value = { ...localFilters.value }
  
  // Fetch rewards with updated filters
  fetchRewards()
}

const resetFilters = () => {
  // Clear any pending debounce timeout
  if (debounceTimeout) {
    clearTimeout(debounceTimeout)
    debounceTimeout = null
  }
  
  // Reset local filters
  localFilters.value = {
    academic_year_id: '',
    type: '',
    search: ''
  }
  
  // Apply reset immediately
  applyFilters()
}

const fetchStudent = async () => {
  try {
    const response = await api.get(`/students/${route.params.studentId}`)
    student.value = response.data.data
    
    // Optional: Set current academic year as default
    if (student.value.current_academic_year_id) {
      localFilters.value.academic_year_id = student.value.current_academic_year_id
    }
  } catch (error) {
    console.error('Error fetching student:', error)
    toast.error(t('errors.fetch_failed'))
  }
}

const getAssignedBy = (reward) => {
  if (reward.teacher?.username) {
    return `${reward.teacher.username} (${t('student_rewards.history.teacher')})`
  }
  
  if (reward.user) {
    const name = reward.user.name || reward.user.username
    const role = reward.user.role?.name || t('student_rewards.history.staff')
    return `${name} (${role})`
  }
  
  return t('student_rewards.history.unknown')
}

const fetchRewards = async () => {
  loading.value = true
  try {
    // Prepare params - convert empty strings to null for cleaner API call
    const params = {
      academic_year_id: filters.value.academic_year_id || null,
      type: filters.value.type || null,
      search: filters.value.search || null
    }
    
    // Log for debugging (remove in production)
    console.log('Fetching rewards with params:', params)
    
    const response = await api.get(`/students/${route.params.studentId}/rewards`, { params })
    rewards.value = response.data.data
    statistics.value = response.data.statistics
    
  } catch (error) {
    console.error('Error fetching rewards:', error)
    toast.error(t('errors.fetch_failed'))
  } finally {
    loading.value = false
  }
}

const fetchAcademicYears = async () => {
  try {
    const response = await api.get('/academic-years')
    academicYears.value = response.data.data
  } catch (error) {
    console.error('Error fetching academic years:', error)
  }
}

const formatDate = (dateString) => {
  if (!dateString) return ''
  const date = new Date(dateString)
  return date.toLocaleDateString('ar')
}

const formatTime = (dateString) => {
  if (!dateString) return ''
  const date = new Date(dateString)
  return date.toLocaleTimeString('ar', { hour: '2-digit', minute: '2-digit' })
}

const getTotalPointsClass = (points) => {
  if (points >= 100) return 'bg-gradient-success'
  if (points >= 50) return 'bg-gradient-info'
  if (points >= 0) return 'bg-gradient-primary'
  return 'bg-gradient-danger'
}

const getPointBadgeClass = (type) => {
  return type === 'positive' ? 'bg-success' : 'bg-danger'
}

const getPointIconClass = (type) => {
  return type === 'positive' ? 'text-success' : 'text-danger'
}

const handleImageError = (event) => {
  event.target.src = '/images/default-avatar.png'
}

onMounted(() => {
  fetchStudent()
  fetchAcademicYears()
  fetchRewards()
})

// Watch for filter changes and apply them
watch(filters, () => {
  fetchRewards()
}, { deep: true })

// Watch local filters for debugging
watch(() => localFilters.value, (newVal) => {
  console.log('Local filters updated:', newVal)
}, { deep: true })
</script>

<style scoped>
/* Your original styles preserved exactly */
/* Stats */
.stat-card {
  border: none;
  border-radius: 1rem;
  color: #fff;
}
.stat-card.bg-success { background: linear-gradient(135deg, #28a745, #20c997); }
.stat-card.bg-danger { background: linear-gradient(135deg, #dc3545, #e83e8c); }
.stat-card.bg-gradient-primary { background: linear-gradient(135deg, #4e73df, #224abe); }
.stat-card.bg-gradient-info { background: linear-gradient(135deg, #36b9cc, #17a2b8); }
.stat-card.bg-gradient-success { background: linear-gradient(135deg, #1cc88a, #13855c); }
.stat-card.bg-gradient-danger { background: linear-gradient(135deg, #e74a3b, #be2617); }
.stat-label { 
  font-size: .85rem; 
  opacity: .9; 
  margin-bottom: 0.25rem;
}
.stat-value { 
  font-size: 1.75rem; 
  font-weight: 700; 
  line-height: 1;
}

/* Chips */
.chip {
  display: inline-flex;
  align-items: center;
  gap: .4rem;
  padding: .25rem .6rem;
  border-radius: 999px;
  background: #f1f3f5;
  font-size: .8rem;
  font-weight: 600;
  color: #495057;
}
.chip.big { padding: .35rem .8rem; font-size: .9rem; }
.chip.compact { font-size: .75rem; padding: .2rem .5rem; }

/* Avatar */
.avatar {
  display: flex;
  align-items: center;
  justify-content: center;
}
.avatar-sm {
  width: 32px;
  height: 32px;
}
.avatar-xl {
  width: 60px;
  height: 60px;
}

/* Badge */
.badge {
  font-size: 0.75rem;
  padding: 0.35em 0.65em;
  font-weight: 600;
}

/* Helpers */
.text-white-75 { color: rgba(255,255,255,.85) !important; }

/* Card */
.card {
  border: 1px solid #e0e0e0;
  border-radius: 0.5rem;
}

.card.shadow-sm {
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075) !important;
}

/* Table */
.table-responsive {
  border-radius: 0.5rem;
  overflow: hidden;
}

.table th {
  background-color: #f8f9fa;
  font-weight: 600;
}

.table td {
  vertical-align: middle;
}

/* Filters */
.form-select-sm {
  padding: 0.25rem 2.25rem 0.25rem 0.75rem;
  font-size: 0.875rem;
  border-radius: 0.25rem;
}

.position-absolute.start-0 {
  left: 0;
}

[dir="rtl"] .position-absolute.start-0 {
  left: auto;
  right: 0;
}

/* Responsive filters */
@media (max-width: 768px) {
  .container-fluid .row.g-2 > div {
    margin-bottom: 0.5rem;
  }
}

/* Adjust for RTL */
[dir="rtl"] .position-relative .form-control-sm {
  padding-left: 0.75rem !important;
  padding-right: 2.5rem !important;
}

[dir="rtl"] .position-relative .form-select {
  padding-left: 0.75rem !important;
  padding-right: 2.25rem !important;
}

[dir="rtl"] .position-absolute.start-0 {
  left: auto !important;
  right: 0 !important;
}

[dir="rtl"] .me-1 {
  margin-left: 0.25rem !important;
  margin-right: 0 !important;
}

[dir="rtl"] .me-2 {
  margin-left: 0.5rem !important;
  margin-right: 0 !important;
}

[dir="rtl"] .me-3 {
  margin-left: 1rem !important;
  margin-right: 0 !important;
}
</style>